import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Wrench, UploadSimple, Plus, Trash, Pencil, ArrowClockwise, Download,
  CheckCircle, Warning, Hourglass, FilePdf, FileXls, FileCsv, Sparkle,
  X, MagnifyingGlass, CaretRight, Tag,
} from "@phosphor-icons/react";

const UNIDADES = ["m²", "m³", "ml", "ud", "h", "kg", "t", "Pa"];

const fmtEuro = (v) => v == null
  ? "—"
  : Number(v).toLocaleString("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });

export default function BaremosReparacionTab({ aseguradoraId, canEdit }) {
  const [baremos, setBaremos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/aseguradoras/${aseguradoraId}/baremos-reparacion`);
      setBaremos(data.baremos || []);
      // Mantener selección si sigue existiendo, refrescando datos del seleccionado
      setSelected((s) => {
        if (!s) return s;
        const next = (data.baremos || []).find((b) => b.id === s.id);
        return next || null;
      });
    } catch (e) { toast.error("Error cargando baremos"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [aseguradoraId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling ligero si hay alguno extracting
  useEffect(() => {
    const hasInProgress = baremos.some((b) => b.status === "pending" || b.status === "extracting");
    if (!hasInProgress) return;
    const t = setInterval(load, 3500);
    return () => clearInterval(t);
  }, [baremos]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDelete = async (b) => {
    if (!window.confirm(`¿Eliminar "${b.name}" y todas sus partidas? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/aseguradoras/${aseguradoraId}/baremos-reparacion/${b.id}`);
      toast.success("Baremo eliminado");
      if (selected?.id === b.id) setSelected(null);
      load();
    } catch (e) { toast.error("Error al eliminar"); }
  };

  const onReextract = async (b) => {
    try {
      await api.post(`/aseguradoras/${aseguradoraId}/baremos-reparacion/${b.id}/reextract`);
      toast.success("Re-extrayendo… la IA está procesando el archivo");
      setTimeout(load, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const toggleActiva = async (b) => {
    try {
      await api.patch(`/aseguradoras/${aseguradoraId}/baremos-reparacion/${b.id}`, { activa: !b.activa });
      load();
    } catch (e) { toast.error("Error"); }
  };

  return (
    <div className="grid grid-cols-[300px_1fr] gap-4" data-testid="baremos-rep-tab">
      <aside className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted">
            Baremos de reparación
          </div>
          {canEdit && (
            <button
              onClick={() => setUploadOpen(true)}
              data-testid="baremos-rep-upload-btn"
              className="btn-brand text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md font-semibold"
            >
              <UploadSimple className="w-3 h-3" weight="bold" /> Subir
            </button>
          )}
        </div>
        {loading ? (
          <div className="text-sm text-muted text-center py-6">Cargando…</div>
        ) : baremos.length === 0 ? (
          <div className="bg-white border border-dashed border-border rounded-md p-4 text-center text-[12px] text-muted">
            <Wrench className="w-8 h-8 mx-auto mb-1 text-muted/60" weight="duotone" />
            Aún no hay tarifas cargadas. Sube un Excel, PDF o CSV.
          </div>
        ) : (
          baremos.map((b) => (
            <button
              key={b.id}
              onClick={() => setSelected(b)}
              data-testid={`baremos-rep-card-${b.id}`}
              className={`w-full text-left bg-white border rounded-md p-2.5 transition-all ${selected?.id === b.id ? "border-amber-400 ring-1 ring-amber-200" : "border-border hover:border-amber-300"}`}
            >
              <div className="flex items-center gap-1.5">
                <div className={`font-semibold text-[12.5px] truncate flex-1 ${b.activa ? "" : "text-muted line-through"}`}>{b.name}</div>
                {b.activa ? (
                  <span className="text-[9px] bg-emerald-50 text-emerald-700 px-1 rounded font-bold">ACTIVO</span>
                ) : (
                  <span className="text-[9px] bg-slate-100 text-slate-600 px-1 rounded">inactivo</span>
                )}
              </div>
              <div className="text-[10.5px] text-muted mt-0.5 flex items-center gap-2">
                <StatusBadge status={b.status} error={b.extract_error} />
                {b.n_partidas > 0 && <span>· {b.n_partidas} partidas</span>}
                {b.vigencia_desde && <span>· desde {b.vigencia_desde}</span>}
              </div>
            </button>
          ))
        )}
      </aside>

      <main className="min-w-0">
        {selected ? (
          <BaremoDetail
            aseguradoraId={aseguradoraId}
            baremo={selected}
            canEdit={canEdit}
            onDelete={() => onDelete(selected)}
            onReextract={() => onReextract(selected)}
            onToggleActiva={() => toggleActiva(selected)}
            onReload={load}
          />
        ) : (
          <div className="bg-white border border-dashed border-border rounded-md p-12 text-center">
            <Wrench className="w-12 h-12 text-muted/60 mx-auto mb-2" weight="duotone" />
            <div className="text-sm text-muted">
              Selecciona un baremo a la izquierda o sube uno nuevo.
            </div>
          </div>
        )}
      </main>

      {uploadOpen && (
        <UploadModal
          aseguradoraId={aseguradoraId}
          onClose={() => setUploadOpen(false)}
          onUploaded={(b) => { setUploadOpen(false); load(); setSelected(b); }}
        />
      )}
    </div>
  );
}

function StatusBadge({ status, error }) {
  const map = {
    pending: ["Pendiente", "bg-slate-100 text-slate-700", null],
    extracting: ["Extrayendo IA…", "bg-amber-50 text-amber-800", Hourglass],
    ready: ["Listo", "bg-emerald-50 text-emerald-700", CheckCircle],
    error: ["Error", "bg-rose-50 text-rose-700", Warning],
    budget_exceeded: ["Saldo IA agotado", "bg-amber-100 text-amber-900", Warning],
  };
  const [label, cls, Icon] = map[status] || map.pending;
  return (
    <span className={`text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${cls}`} title={error || ""} data-testid={`baremo-status-${status}`}>
      {Icon && <Icon className="w-2.5 h-2.5" weight="fill" />} {label}
    </span>
  );
}

function BaremoDetail({ aseguradoraId, baremo, canEdit, onDelete, onReextract, onToggleActiva, onReload }) {
  const [data, setData] = useState({ partidas: [], capitulos: [] });
  const [q, setQ] = useState("");
  const [cap, setCap] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (cap) params.set("capitulo", cap);
      const { data: res } = await api.get(
        `/aseguradoras/${aseguradoraId}/baremos-reparacion/${baremo.id}/partidas?${params.toString()}`,
      );
      setData(res);
    } catch (e) { toast.error("Error cargando partidas"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [baremo.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); }, [q, cap]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDeletePartida = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.descripcion}"?`)) return;
    try {
      await api.delete(`/aseguradoras/${aseguradoraId}/baremos-reparacion/${baremo.id}/partidas/${p.id}`);
      toast.success("Partida eliminada");
      load(); onReload?.();
    } catch (e) { toast.error("Error"); }
  };

  return (
    <div className="space-y-3">
      {/* Cabecera del baremo */}
      <header className="bg-white border border-border rounded-md p-3 flex flex-wrap items-start gap-3" data-testid="baremos-rep-detail-header">
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-[15px]">{baremo.name}</div>
          {baremo.descripcion && <div className="text-[12px] text-muted mt-0.5">{baremo.descripcion}</div>}
          <div className="flex flex-wrap items-center gap-3 mt-1.5 text-[10.5px] text-muted">
            <StatusBadge status={baremo.status} error={baremo.extract_error} />
            {baremo.n_partidas > 0 && <span>{baremo.n_partidas} partidas</span>}
            {baremo.vigencia_desde && <span>Vigencia desde: <b>{baremo.vigencia_desde}</b></span>}
            {baremo.vigencia_hasta && <span>hasta: <b>{baremo.vigencia_hasta}</b></span>}
            {baremo.source_filename && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/aseguradoras/${aseguradoraId}/baremos-reparacion/${baremo.id}/file`}
                target="_blank" rel="noreferrer"
                className="text-amber-700 hover:underline inline-flex items-center gap-1"
                data-testid="baremos-rep-source"
              >
                <Download className="w-2.5 h-2.5" weight="bold" /> {baremo.source_filename}
              </a>
            )}
          </div>
          {baremo.extract_error && baremo.status === "budget_exceeded" && (
            <div className="text-[11px] text-amber-900 mt-1.5 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 flex items-start gap-1.5" data-testid="baremo-budget-alert">
              <Warning className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" weight="fill" />
              <div>
                <b>Saldo de Universal Key agotado.</b> {baremo.extract_error}
                {baremo.source_path && (
                  <span> Cuando recargues, pulsa el icono <ArrowClockwise className="w-3 h-3 inline-block align-text-bottom" weight="bold" /> para reintentar.</span>
                )}
              </div>
            </div>
          )}
          {baremo.extract_error && baremo.status === "error" && (
            <div className="text-[10.5px] text-rose-700 mt-1">Detalle: {baremo.extract_error}</div>
          )}
        </div>
        {canEdit && (
          <div className="flex items-center gap-1">
            <button onClick={onToggleActiva} title={baremo.activa ? "Marcar como inactivo" : "Marcar como activo"} data-testid="baremos-rep-toggle-activa" className="text-[11px] px-2 py-1 rounded border border-border bg-white hover:bg-amber-50">
              {baremo.activa ? "Desactivar" : "Activar"}
            </button>
            {baremo.source_path && (
              <button onClick={onReextract} title="Volver a extraer con IA" data-testid="baremos-rep-reextract" className="text-muted hover:text-foreground p-1.5">
                <ArrowClockwise className="w-4 h-4" weight="bold" />
              </button>
            )}
            <button onClick={onDelete} title="Eliminar baremo" data-testid="baremos-rep-delete" className="text-muted hover:text-rose-600 p-1.5">
              <Trash className="w-4 h-4" weight="bold" />
            </button>
          </div>
        )}
      </header>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[260px]">
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por descripción, código o capítulo…"
            data-testid="baremos-rep-search"
            className="w-full pl-8 pr-3 py-1.5 text-[12px]"
          />
          <MagnifyingGlass className="w-4 h-4 absolute left-2.5 top-2 text-muted" />
        </div>
        <select value={cap} onChange={(e) => setCap(e.target.value)} data-testid="baremos-rep-capitulo" className="text-[12px]">
          <option value="">Todos los capítulos</option>
          {data.capitulos.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {canEdit && (
          <button onClick={() => setAdding(true)} data-testid="baremos-rep-add-partida" className="btn-brand text-[11px] inline-flex items-center gap-1 px-2 py-1.5 rounded-md font-semibold">
            <Plus className="w-3 h-3" weight="bold" /> Añadir partida
          </button>
        )}
      </div>

      {/* Tabla partidas */}
      {loading ? (
        <div className="text-center text-muted text-sm py-6">Cargando partidas…</div>
      ) : data.partidas.length === 0 ? (
        <div className="bg-white border border-dashed border-border rounded-md p-6 text-center text-[12px] text-muted">
          {baremo.status === "extracting"
            ? "La IA está extrayendo las partidas… esto puede tardar 30-60s. Recarga en unos segundos."
            : "No hay partidas en este baremo."}
        </div>
      ) : (
        <div className="bg-white border border-border rounded-md overflow-x-auto" data-testid="baremos-rep-table">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-muted">
              <tr>
                <th className="text-left px-3 py-2">Capítulo</th>
                <th className="text-left px-3 py-2">Código</th>
                <th className="text-left px-3 py-2">Descripción</th>
                <th className="text-left px-2 py-2">UD</th>
                <th className="text-right px-3 py-2">Precio</th>
                <th className="text-right px-3 py-2">M.O.</th>
                <th className="text-right px-3 py-2">Material</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {data.partidas.map((p) => (
                <tr key={p.id} className="hover:bg-amber-50/30" data-testid={`baremos-rep-row-${p.id}`}>
                  <td className="px-3 py-1.5">
                    {p.capitulo && (
                      <span className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                        <Tag className="w-2.5 h-2.5" weight="fill" /> {p.capitulo}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-[10.5px]">{p.codigo || "—"}</td>
                  <td className="px-3 py-1.5">{p.descripcion}</td>
                  <td className="px-2 py-1.5 text-muted">{p.unidad || "—"}</td>
                  <td className="px-3 py-1.5 text-right font-mono font-semibold">{fmtEuro(p.precio_unitario)}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-muted">{fmtEuro(p.precio_mano_obra)}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-muted">{fmtEuro(p.precio_material)}</td>
                  <td className="px-2 py-1.5 text-right">
                    {canEdit && (
                      <>
                        <button onClick={() => setEditing(p)} className="text-muted hover:text-foreground p-1" title="Editar"><Pencil className="w-3 h-3" weight="bold" /></button>
                        <button onClick={() => onDeletePartida(p)} className="text-muted hover:text-rose-600 p-1" title="Eliminar"><Trash className="w-3 h-3" weight="bold" /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(editing || adding) && (
        <PartidaModal
          aseguradoraId={aseguradoraId}
          baremoId={baremo.id}
          partida={editing}
          onClose={() => { setEditing(null); setAdding(false); }}
          onSaved={() => { setEditing(null); setAdding(false); load(); onReload?.(); }}
        />
      )}
    </div>
  );
}

function UploadModal({ aseguradoraId, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Selecciona archivo"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (name) fd.append("name", name);
      if (descripcion) fd.append("descripcion", descripcion);
      if (desde) fd.append("vigencia_desde", desde);
      if (hasta) fd.append("vigencia_hasta", hasta);
      const { data } = await api.post(`/aseguradoras/${aseguradoraId}/baremos-reparacion/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      toast.success(ext === "csv" ? "Baremo subido y partidas mapeadas" : "Baremo subido · la IA está extrayendo las partidas");
      onUploaded(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error subiendo el baremo");
    } finally { setBusy(false); }
  };

  const ext = file ? (file.name.split(".").pop() || "").toLowerCase() : "";

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-lg w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="baremos-rep-upload-modal">
        <div className="px-5 pt-4 pb-3 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold">Subir baremo de reparación</h2>
          <button type="button" onClick={onClose}><X className="w-4 h-4" weight="bold" /></button>
        </div>
        <div className="px-5 py-4 space-y-3 text-sm">
          <label className="block">
            <span className="text-[11px] font-semibold text-muted block mb-1">Archivo (Excel · PDF · CSV · máx 30 MB)</span>
            <input type="file" accept=".pdf,.xlsx,.xlsm,.csv" onChange={(e) => setFile(e.target.files[0])} required data-testid="baremos-rep-upload-file" className="w-full text-[11px]" />
            {ext && (
              <div className="text-[10.5px] text-muted mt-1 inline-flex items-center gap-1">
                {ext === "pdf" && <><FilePdf className="w-3 h-3 text-rose-600" weight="fill" /> PDF · La IA extraerá automáticamente las partidas (~30-60s)</>}
                {(ext === "xlsx" || ext === "xlsm") && <><FileXls className="w-3 h-3 text-emerald-700" weight="fill" /> Excel · La IA extraerá automáticamente las partidas (~30-60s)</>}
                {ext === "csv" && <><FileCsv className="w-3 h-3 text-sky-700" weight="fill" /> CSV · Mapeo directo de columnas (instantáneo)</>}
              </div>
            )}
          </label>
          <label className="block">
            <span className="text-[11px] font-semibold text-muted block mb-1">Nombre (opcional)</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="ej. Tarifa Mapfre Hogar 2026" data-testid="baremos-rep-upload-name" className="w-full text-[12px]" />
          </label>
          <label className="block">
            <span className="text-[11px] font-semibold text-muted block mb-1">Descripción</span>
            <input value={descripcion} onChange={(e) => setDescripcion(e.target.value)} placeholder="Tarifa vigente para reparadores autorizados" className="w-full text-[12px]" />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="text-[11px] font-semibold text-muted block mb-1">Vigencia desde</span>
              <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="w-full text-[12px]" /></label>
            <label className="block"><span className="text-[11px] font-semibold text-muted block mb-1">Vigencia hasta</span>
              <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="w-full text-[12px]" /></label>
          </div>
          {ext === "csv" && (
            <div className="text-[10.5px] text-muted bg-sky-50 border border-sky-200 rounded p-2">
              <b>Columnas esperadas en CSV</b> (cabeceras flexibles, separadas por ; o ,):<br />
              <code className="text-[10px]">Capítulo · Código · Descripción · Unidad · Precio Unitario · Mano de Obra · Material · Notas</code>
            </div>
          )}
          {(ext === "pdf" || ext === "xlsx" || ext === "xlsm") && (
            <div className="text-[10.5px] text-amber-900 bg-amber-50 border border-amber-200 rounded p-2">
              <Sparkle className="w-3 h-3 inline mr-1" weight="fill" />
              Gemini 2.5 Flash analizará el archivo y extraerá automáticamente las partidas estructuradas. Podrás revisarlas y editarlas después.
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border bg-surface/30">
          <button type="button" onClick={onClose} className="text-xs px-3 py-2 text-muted hover:text-foreground">Cancelar</button>
          <button type="submit" disabled={busy} data-testid="baremos-rep-upload-submit" className="btn-brand text-xs px-4 py-2 rounded-md font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
            <UploadSimple className="w-3 h-3" weight="bold" /> {busy ? "Subiendo…" : "Subir"}
          </button>
        </div>
      </form>
    </div>
  );
}

function PartidaModal({ aseguradoraId, baremoId, partida, onClose, onSaved }) {
  const init = partida || { capitulo: "", codigo: "", descripcion: "", unidad: "m²", precio_unitario: "", precio_mano_obra: "", precio_material: "", notas: "" };
  const [form, setForm] = useState(init);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.descripcion?.trim()) { toast.error("Descripción obligatoria"); return; }
    setBusy(true);
    const body = {
      capitulo: form.capitulo || null,
      codigo: form.codigo || null,
      descripcion: form.descripcion,
      unidad: form.unidad || null,
      precio_unitario: form.precio_unitario === "" ? null : parseFloat(form.precio_unitario),
      precio_mano_obra: form.precio_mano_obra === "" ? null : parseFloat(form.precio_mano_obra),
      precio_material: form.precio_material === "" ? null : parseFloat(form.precio_material),
      notas: form.notas || null,
    };
    try {
      if (partida) {
        await api.patch(`/aseguradoras/${aseguradoraId}/baremos-reparacion/${baremoId}/partidas/${partida.id}`, body);
      } else {
        await api.post(`/aseguradoras/${aseguradoraId}/baremos-reparacion/${baremoId}/partidas`, body);
      }
      toast.success("Partida guardada");
      onSaved();
    } catch (e) { toast.error("Error guardando"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-lg w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="baremos-rep-partida-modal">
        <div className="px-5 pt-4 pb-3 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold">{partida ? "Editar partida" : "Nueva partida"}</h2>
          <button type="button" onClick={onClose}><X className="w-4 h-4" weight="bold" /></button>
        </div>
        <div className="px-5 py-4 grid grid-cols-2 gap-3 text-sm">
          <label className="block col-span-2"><span className="text-[11px] font-semibold text-muted block mb-1">Descripción *</span>
            <input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} required data-testid="baremos-rep-partida-desc" className="w-full text-[12px]" /></label>
          <label className="block"><span className="text-[11px] font-semibold text-muted block mb-1">Capítulo</span>
            <input value={form.capitulo || ""} onChange={(e) => setForm({ ...form, capitulo: e.target.value })} list="bp-capitulos" placeholder="ej. PINTURA" className="w-full text-[12px]" /></label>
          <label className="block"><span className="text-[11px] font-semibold text-muted block mb-1">Código</span>
            <input value={form.codigo || ""} onChange={(e) => setForm({ ...form, codigo: e.target.value })} className="w-full text-[12px]" /></label>
          <label className="block"><span className="text-[11px] font-semibold text-muted block mb-1">Unidad</span>
            <input list="bp-unidades" value={form.unidad || ""} onChange={(e) => setForm({ ...form, unidad: e.target.value })} className="w-full text-[12px]" /></label>
          <label className="block"><span className="text-[11px] font-semibold text-muted block mb-1">Precio unitario (€)</span>
            <input type="number" step="0.01" value={form.precio_unitario ?? ""} onChange={(e) => setForm({ ...form, precio_unitario: e.target.value })} className="w-full text-[12px] text-right font-mono" /></label>
          <label className="block"><span className="text-[11px] font-semibold text-muted block mb-1">Mano de obra (€)</span>
            <input type="number" step="0.01" value={form.precio_mano_obra ?? ""} onChange={(e) => setForm({ ...form, precio_mano_obra: e.target.value })} className="w-full text-[12px] text-right font-mono" /></label>
          <label className="block"><span className="text-[11px] font-semibold text-muted block mb-1">Material (€)</span>
            <input type="number" step="0.01" value={form.precio_material ?? ""} onChange={(e) => setForm({ ...form, precio_material: e.target.value })} className="w-full text-[12px] text-right font-mono" /></label>
          <label className="block col-span-2"><span className="text-[11px] font-semibold text-muted block mb-1">Notas</span>
            <input value={form.notas || ""} onChange={(e) => setForm({ ...form, notas: e.target.value })} className="w-full text-[12px]" /></label>
          <datalist id="bp-unidades">{UNIDADES.map((u) => <option key={u} value={u} />)}</datalist>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border bg-surface/30">
          <button type="button" onClick={onClose} className="text-xs px-3 py-2 text-muted hover:text-foreground">Cancelar</button>
          <button type="submit" disabled={busy} data-testid="baremos-rep-partida-submit" className="btn-brand text-xs px-4 py-2 rounded-md font-semibold disabled:opacity-50">{busy ? "Guardando…" : "Guardar"}</button>
        </div>
      </form>
    </div>
  );
}
